// Use a configurable base for API calls.
// In dev with Vite proxy, keep VITE_API_BASE empty.
const API_BASE = (import.meta as any)?.env?.VITE_API_BASE || "";

// Join API base and path safely (avoid double slashes)
function buildUrl(path: string) {
  if (!API_BASE) return path;
  const base = API_BASE.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

// Safe JSON parse with debug logs
async function safeParseJson(res: Response, ctx: string) {
  try {
    const data = await res.json();
    console.log(`[Auth] ${ctx} parsed JSON`, data);
    return data;
  } catch (err) {
    console.warn(`[Auth] ${ctx} JSON parse failed`, err);
    const text = await res.text().catch(() => "");
    return { _text: text || "" };
  }
}

export async function loginWithPassword(email: string, password: string): Promise<{
  success: boolean;
  error?: string;
  redirectTo?: string;
}> {
  const url = buildUrl("/api/auth/login");
  console.log("[Auth] staff login start", {
    url,
    emailMasked: email.replace(/(.{2}).+(@.*)/, "$1***$2"),
  });

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin", // with Vite proxy this is correct
      body: JSON.stringify({ email, password }),
    });

    const data: any = await safeParseJson(res, "staff login");
    console.log("[Auth] staff login response", { status: res.status });

    if (!res.ok) {
      return { success: false, error: data?.error || "Login failed" };
    }

    return { success: true, redirectTo: data?.redirectTo || "/staff/dashboard" };
  } catch (err) {
    console.error("[Auth] staff login error", err);
    return { success: false, error: "Network error" };
  }
}

export async function logout(): Promise<boolean> {
  const url = buildUrl("/api/auth/logout");
  console.log("[Auth] logout start", { url });

  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
    });
    console.log("[Auth] logout response", { status: res.status });
    return res.ok;
  } catch (err) {
    console.error("[Auth] logout error", err);
    return false;
  }
}

// Optional: quick connectivity checks (use in dev console or a debug button)
export async function pingHealth(): Promise<{ ok: boolean; status: number; body: any }> {
  const url = buildUrl("/api/health");
  console.log("[Auth] ping /api/health", { url });
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    const body = await safeParseJson(res, "ping health");
    return { ok: res.ok, status: res.status, body };
  } catch (err) {
    console.error("[Auth] ping health error", err);
    return { ok: false, status: 0, body: { error: String(err) } };
  }
}

export async function pingAuthRouter(): Promise<{ ok: boolean; status: number; body: any }> {
  const url = buildUrl("/api/auth/__ping");
  console.log("[Auth] ping /api/auth/__ping", { url });
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    const body = await safeParseJson(res, "ping auth router");
    return { ok: res.ok, status: res.status, body };
  } catch (err) {
    console.error("[Auth] ping auth router error", err);
    return { ok: false, status: 0, body: { error: String(err) } };
  }
}